/**
 * useTeamEvidenceEnforcement
 *
 * Reads team-level evidence settings and exposes enforcement helpers
 * for source control, collection creation, country/jurisdiction, and
 * clinical suggestions.
 *
 * In-session Sources / search (usage surfaces) apply the same
 * enabled/changeable rules for every role — org/team-disabled sources
 * stay hidden for team admins too (EVI-2123 / EVI-2127).
 *
 * When the org has overridden `team_evidence_settings`
 * (`org_team_evidence_settings` + `readonly_fields`), the org lock state
 * takes precedence over the team-level `source_control_config` for each
 * source bucket (EVI-2196). An org that enables-but-doesn't-force a
 * source (`managed`) shows it ON and editable in-session; an org that
 * forces it (`forced_on`) locks it ON; an org that disables it stays
 * hidden. When the org has no opinion (`none`), the team config applies
 * as before.
 *
 * Personal-settings locks (clinical suggestions, country/jurisdiction)
 * and team-collection creation still bypass for admins/owners so they
 * can administer policy without being locked by it.
 */

import {
  useIsEvidencePerCollectionLibraryModesEnabled,
  useIsTeamEvidenceSettingsEnabled,
} from '@/features/flags';
import {
  getOrgClinicalSourcesLockState,
  getOrgClinicalWebLockState,
  getOrgIntegrationPartnersLockState,
  orgLockStateToDirective,
  resolveAllowlistToUnifiedIds,
  resolveManagedEnabled,
  type OrgSourceDirective,
  type TeamSourceControlConfigSlice,
} from '@heidi/evidence/contracts';
import { useCollections } from '@heidi/evidence/data';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useMemo } from 'react';

type SourceBucketFlags = { enabled: boolean; changeable: boolean };

/**
 * Resolve a source bucket's in-session flags from the org directive and
 * team-level config.
 *
 * - `forced_on` → org mandates the source: ON and locked, team config ignored.
 * - `disabled`  → org disabled the source: hidden, team config ignored.
 * - `managed`   → org default: `changeable=true` (editable per-query), but
 *                 `enabled` comes from the team config — a team admin may have
 *                 toggled the source off on the Settings page
 *                 (`isOrgValuePinned('managed')` is false, so the toggle stays
 *                 editable there), and that opt-out must persist in-session.
 * - `null`      → no org opinion; team config applies (existing behaviour).
 */
function resolveBucket(
  isEnforced: boolean | undefined,
  orgDirective: OrgSourceDirective,
  teamEnabled: boolean,
  teamChangeable: boolean
): SourceBucketFlags {
  if (!isEnforced) return { enabled: true, changeable: true };
  switch (orgDirective?.kind) {
    case 'forced_on':
      return { enabled: true, changeable: false };
    case 'disabled':
      return { enabled: false, changeable: false };
    case 'managed':
      // Org guarantees editability; team config decides the default on/off.
      return { enabled: teamEnabled, changeable: true };
    default:
      return { enabled: teamEnabled, changeable: teamChangeable };
  }
}

function areLibraryCollectionIdListsActive(
  libraryChangeable: boolean,
  libraryCollectionIdsChangeable: boolean
): boolean {
  return libraryChangeable === false || libraryCollectionIdsChangeable === false;
}

function keepLiveCollectionIds(
  ids: string[] | null,
  liveIds: Set<string> | null
): string[] | null {
  if (ids == null) {
    return null;
  }
  if (liveIds == null) {
    return ids;
  }
  const kept = ids.filter((id) => liveIds.has(id));
  return kept.length > 0 ? kept : null;
}

export const useTeamEvidenceEnforcement = () => {
  const { teamDetails, hasTeam, isTeamAdmin, isTeamOwner } =
    useCombinedQueriesTeamGeneralInfo();
  const isPerCollectionModesEnabled = useIsEvidencePerCollectionLibraryModesEnabled();

  const isEnabled = useIsTeamEvidenceSettingsEnabled();
  const settings = teamDetails?.team?.team_evidence_settings;
  const sourceConfig = settings?.source_control_config;
  const { data: collectionsData } = useCollections({
    enabled:
      isEnabled &&
      hasTeam &&
      ((sourceConfig?.enforced_web_collection_ids?.length ?? 0) > 0 ||
        (sourceConfig?.enforced_library_collection_ids?.length ?? 0) > 0 ||
        (sourceConfig?.optional_library_collection_ids?.length ?? 0) > 0),
  });

  // Org cascade: `org_team_evidence_settings` holds the org's
  // `source_control_config`; `readonly_fields` marks the team-settings
  // field as org-overridden. Both come from the team GET response.
  const orgSourceControlConfig: TeamSourceControlConfigSlice | null | undefined =
    teamDetails?.team?.org_team_evidence_settings?.source_control_config;
  const readonlyFields = teamDetails?.team?.readonly_fields;

  const hasTeamPolicy = isEnabled && hasTeam;
  const isAdminOrOwner = Boolean(isTeamAdmin || isTeamOwner);

  // Usage surfaces (Sources picker, search sanitization): all roles.
  const isEnforced = hasTeamPolicy;
  // Personal settings / member-only locks: still skip admins/owners.
  const enforcePersonalSettings = hasTeamPolicy && !isAdminOrOwner;

  // Org lock states per bucket — `none` means the org hasn't overridden
  // team_evidence_settings, so team config applies. The raw lock states are
  // reused by `resolveManagedEnabled` so the in-session picker and the
  // Settings form share one derivation for the `managed` + tri-state contract.
  const orgClinicalSourcesLockState = useMemo(
    () =>
      getOrgClinicalSourcesLockState(undefined, orgSourceControlConfig, readonlyFields),
    [orgSourceControlConfig, readonlyFields]
  );
  const orgClinicalWebLockState = useMemo(
    () => getOrgClinicalWebLockState(undefined, orgSourceControlConfig, readonlyFields),
    [orgSourceControlConfig, readonlyFields]
  );
  const orgIntegrationPartnersLockState = useMemo(
    () =>
      getOrgIntegrationPartnersLockState(
        undefined,
        orgSourceControlConfig,
        readonlyFields
      ),
    [orgSourceControlConfig, readonlyFields]
  );
  const orgClinicalSourcesDirective = useMemo(
    () => orgLockStateToDirective(orgClinicalSourcesLockState),
    [orgClinicalSourcesLockState]
  );
  const orgClinicalWebDirective = useMemo(
    () => orgLockStateToDirective(orgClinicalWebLockState),
    [orgClinicalWebLockState]
  );
  const orgIntegrationPartnersDirective = useMemo(
    () => orgLockStateToDirective(orgIntegrationPartnersLockState),
    [orgIntegrationPartnersLockState]
  );

  // Fail-open: when sourceConfig is null/missing, `?? true` ensures all sources
  // remain visible. This prevents accidentally hiding UI when the backend hasn't
  // populated config yet — same pattern used by webCollections and integrationPartners.
  const sourceControl = useMemo(
    () => ({
      /**
       * Consolidated clinical sources (guidelines + primary evidence).
       * When enforced, reads `local_guidelines_enabled` and `primary_evidence_enabled`
       * from backend — both must be true for the combined toggle to be visible.
       */
      clinicalSources: resolveBucket(
        isEnforced,
        orgClinicalSourcesDirective,
        // ON only when both sub-toggles are effectively ON — same derivation as
        // the Settings form (`resolveManagedEnabled`), so a mixed tri-state
        // (one `null`, one explicit `false`) shows OFF on both surfaces.
        resolveManagedEnabled(
          orgClinicalSourcesLockState,
          sourceConfig?.local_guidelines_enabled ?? null,
          orgSourceControlConfig?.local_guidelines_enabled
        ) &&
          resolveManagedEnabled(
            orgClinicalSourcesLockState,
            sourceConfig?.primary_evidence_enabled ?? null,
            orgSourceControlConfig?.primary_evidence_enabled
          ),
        (sourceConfig?.local_guidelines_changeable ?? true) &&
          (sourceConfig?.primary_evidence_changeable ?? true)
      ),
      /** @deprecated Use `clinicalSources` instead. Kept for gradual migration. */
      localGuidelines: resolveBucket(
        isEnforced,
        null,
        sourceConfig?.local_guidelines_enabled ?? true,
        sourceConfig?.local_guidelines_changeable ?? true
      ),
      /** @deprecated Use `clinicalSources` instead. Kept for gradual migration. */
      primaryEvidence: resolveBucket(
        isEnforced,
        null,
        sourceConfig?.primary_evidence_enabled ?? true,
        sourceConfig?.primary_evidence_changeable ?? true
      ),
      library: {
        enabled: isEnforced ? (sourceConfig?.library_enabled ?? true) : true,
        changeable: isEnforced ? (sourceConfig?.library_changeable ?? true) : true,
      },
      webCollections: resolveBucket(
        isEnforced,
        orgClinicalWebDirective,
        resolveManagedEnabled(
          orgClinicalWebLockState,
          sourceConfig?.web_collections_enabled ?? null,
          orgSourceControlConfig?.web_collections_enabled
        ),
        sourceConfig?.web_collections_changeable ?? true
      ),
      integrationPartners: resolveBucket(
        isEnforced,
        orgIntegrationPartnersDirective,
        resolveManagedEnabled(
          orgIntegrationPartnersLockState,
          sourceConfig?.integration_partners_enabled ?? null,
          orgSourceControlConfig?.integration_partners_enabled
        ),
        sourceConfig?.integration_partners_changeable ?? true
      ),
    }),
    [
      sourceConfig,
      isEnforced,
      orgSourceControlConfig,
      orgClinicalSourcesDirective,
      orgClinicalSourcesLockState,
      orgClinicalWebDirective,
      orgClinicalWebLockState,
      orgIntegrationPartnersDirective,
      orgIntegrationPartnersLockState,
    ]
  );

  // Fail-open on empty allowlist: an admin who hasn't curated a list yet
  // (backend returns `[]`) should not cause every team-shared collection to
  // disappear for members. Treat `[]` the same as `null` (no enforcement) —
  // consistent with the per-source `?? true` fail-open pattern above.
  const libraryListsActive = areLibraryCollectionIdListsActive(
    sourceConfig?.library_changeable ?? true,
    sourceConfig?.library_collection_ids_changeable ?? true
  );

  const liveLibraryCollectionIds = useMemo(() => {
    const collections = collectionsData?.collections;
    if (!collections) {
      return null;
    }
    return new Set(collections.map((collection) => collection.collection_id));
  }, [collectionsData?.collections]);

  const enforcedLibraryCollectionIds = useMemo(() => {
    if (!isEnforced) return null;
    if (isPerCollectionModesEnabled && !libraryListsActive) return null;
    const ids = sourceConfig?.enforced_library_collection_ids;
    return keepLiveCollectionIds(
      ids && ids.length > 0 ? ids : null,
      liveLibraryCollectionIds
    );
  }, [
    sourceConfig,
    isEnforced,
    isPerCollectionModesEnabled,
    libraryListsActive,
    liveLibraryCollectionIds,
  ]);

  const optionalLibraryCollectionIds = useMemo(() => {
    if (!isEnforced || !isPerCollectionModesEnabled) return null;
    if (!libraryListsActive) return null;
    const ids = sourceConfig?.optional_library_collection_ids;
    return keepLiveCollectionIds(
      ids && ids.length > 0 ? ids : null,
      liveLibraryCollectionIds
    );
  }, [
    sourceConfig,
    isEnforced,
    isPerCollectionModesEnabled,
    libraryListsActive,
    liveLibraryCollectionIds,
  ]);

  // Allowlist for visibility filtering = enforced ∪ optional under new modes.
  const libraryCollectionAllowlistIds = useMemo(() => {
    if (!isEnforced) return null;
    if (isPerCollectionModesEnabled) {
      if (!libraryListsActive) return null;
      const enforced = sourceConfig?.enforced_library_collection_ids ?? [];
      const optional = sourceConfig?.optional_library_collection_ids ?? [];
      const merged = [...new Set([...enforced, ...optional])];
      return keepLiveCollectionIds(
        merged.length > 0 ? merged : null,
        liveLibraryCollectionIds
      );
    }
    return enforcedLibraryCollectionIds;
  }, [
    isEnforced,
    isPerCollectionModesEnabled,
    libraryListsActive,
    sourceConfig,
    enforcedLibraryCollectionIds,
    liveLibraryCollectionIds,
  ]);

  const libraryCollectionIdsChangeable = isEnforced
    ? isPerCollectionModesEnabled
      ? (optionalLibraryCollectionIds?.length ?? 0) > 0 ||
        !(enforcedLibraryCollectionIds?.length ?? 0)
      : (sourceConfig?.library_collection_ids_changeable ?? true)
    : true;

  const enforcedWebCollectionIds = useMemo(() => {
    if (!isEnforced) return null;
    const ids = sourceConfig?.enforced_web_collection_ids;
    if (!ids || ids.length === 0) return null;
    return resolveAllowlistToUnifiedIds(
      ids,
      (collectionsData?.collections ?? []).map((collection) => ({
        id: collection.collection_id,
        legacySourceListId: collection.legacy_source_list_id,
      }))
    );
  }, [sourceConfig, isEnforced, collectionsData?.collections]);

  const webCollectionIdsChangeable = isEnforced
    ? (sourceConfig?.web_collection_ids_changeable ?? true)
    : true;

  // Admins/owners manage the library; keep create capability even when
  // team policy restricts members from creating team collections.
  const canCreateTeamCollection =
    isAdminOrOwner || !hasTeamPolicy
      ? true
      : (settings?.allow_team_collection_creation ?? false);

  const evidenceCountry = useMemo(
    () => ({
      value: settings?.evidence_country ?? null,
      enforced: enforcePersonalSettings && (settings?.enforce_evidence_country ?? false),
    }),
    [settings, enforcePersonalSettings]
  );

  const evidenceJurisdiction = useMemo(
    () => ({
      value: settings?.evidence_jurisdiction ?? null,
      enforced:
        enforcePersonalSettings &&
        (settings?.enforce_evidence_jurisdiction ?? false) &&
        (settings?.enforce_evidence_country ?? false),
    }),
    [settings, enforcePersonalSettings]
  );

  const clinicalSuggestions = useMemo(
    () => ({
      enforced:
        enforcePersonalSettings && (settings?.enforce_clinical_suggestions ?? false),
      hidden: enforcePersonalSettings && (settings?.hide_suggestions ?? false),
    }),
    [settings, enforcePersonalSettings]
  );

  const defaultLibraryEnabled = settings?.default_library_enabled ?? false;

  const teamName = teamDetails?.team?.name;

  return {
    /** Whether team source policy applies on usage surfaces (all roles) */
    isEnforced,
    /** Per-source enabled/changeable flags (guidelines+primary consolidated into `clinicalSources`) */
    sourceControl,
    /** Enforced library collection IDs (null = no enforcement) */
    enforcedLibraryCollectionIds,
    /** Optional library collection IDs (EVI-1958; null when flag off / unset) */
    optionalLibraryCollectionIds,
    /** Visibility allowlist = enforced ∪ optional when per-collection modes on */
    libraryCollectionAllowlistIds,
    libraryCollectionIdsChangeable,
    libraryCollectionIdListsActive: libraryListsActive,
    /** Enforced web collection IDs (null = no enforcement) */
    enforcedWebCollectionIds,
    webCollectionIdsChangeable,
    canCreateTeamCollection,
    evidenceCountry,
    evidenceJurisdiction,
    clinicalSuggestions,
    defaultLibraryEnabled,
    teamName,
    isPerCollectionLibraryModesEnabled: isPerCollectionModesEnabled,
  };
};
